import React, { forwardRef, useEffect } from 'react';
import {
  func, string, object, bool,
} from 'prop-types';
import isEmpty from 'lodash/fp/isEmpty';
import prop from 'lodash/fp/prop';
import noop from 'lodash/fp/noop';
import indexOf from 'lodash/indexOf';
import compose from 'lodash/fp/compose';
import curryRight from 'lodash/fp/curryRight';
import Grid from '@active/react-ui/Grid/Grid';
import GridItem from '@active/react-ui/GridItem/GridItem';
import FormGroup from '@active/react-ui/FormGroup';
import FormLabel from '@active/react-ui/FormLabel';
import FormField from '@active/react-ui/FormField';
import { connectForm } from '@active/react-ui/Form';
import Dropdown from '@active/react-ui/ResponsiveDropdown';
import Locale from '@active/react-utils/i18n/Locale';
import AddressStatic from '@active/react-address-editor/components/AddressStatic';
import useAddress from '@active/react-address-editor/components/useAddress';
import TwoColsField from '../TwoColsField';
import MenuItemsList from './components/MenuItemsList';

export const ONECOLFIELDS = [ 'line1', 'line2', 'line21', 'line22', 'email' ];

const AddressEditor = (props) => {
  const {
    fields,
    address,
    countries,
    onCountryChange,
  } = useAddress(props);

  const {
    name,
    static: staticDisplay,
    required,
    fieldsConfig = {},
    renderReadyCallback = noop,
  } = props;

  useEffect(() => {
    countries.length > 0 && renderReadyCallback();
  }, [ countries.length, renderReadyCallback ]);

  const FIELD_PREFIX = 'addressForm.addressField';
  if (staticDisplay) {
    return <AddressStatic address={address} />;
  }

  return isEmpty(countries) ? null : (<React.Fragment>
    <Grid rowGap={0} container={false} className="address-editor">
      <GridItem>
        <FormGroup
          key="country"
          name={`${name}.country`}
          required={required} withoutLayout>
          <FormLabel htmlFor="country"><Locale id={`${FIELD_PREFIX}.country`} /></FormLabel>
          <FormField
            id="country"
            as={Dropdown}
            onChange={onCountryChange}
            {...fieldsConfig.country}>
            {countries && MenuItemsList({ data: countries })}
          </FormField>
        </FormGroup>
      </GridItem>
      {
        address.country
          && fields.map((field) => {
            const isTwoColsField = indexOf(ONECOLFIELDS, field.name) === -1;

            return (
              <TwoColsField field={field} key={field.name} name={name}
                fieldConfig={fieldsConfig[field.name]} isTwoColsField={isTwoColsField}/>
            );
          })
      }
    </Grid>
  </React.Fragment>
  );
};

AddressEditor.propTypes = {
  /** Name of the address, map the value in the `Form` */
  name: string,
  /** Toggle read only version */
  static: bool,
  /** Is its value is required */
  required: bool,
  /** Props for each field */
  fieldsConfig: object,
  /** Props for each field */
  renderReadyCallback: func,
};

const AddressEditorContainer = compose(
  curryRight(connectForm)(
    ({ form: { values, setFieldValue, setFieldTouched } = {} }, { name }) => ({
      value: prop(name)(values),
      onFieldValueChange: setFieldValue,
      onFieldTouch: setFieldTouched,
    }),
  ),
  forwardRef,
)(AddressEditor);

export default AddressEditorContainer;
